import type { createFileRoute } from '@tanstack/react-router';
import { redirect } from '@tanstack/react-router';
import { Schema, Predicate } from 'effect';
import { optionalCatching } from '@/lib/search-params';
import { rootLogger } from '@/lib/root-logger';
import { getSession } from '@/services/get-session';
import {
  BillingPeriod,
  rememberBillingPeriod,
} from '@/lib/checkout/billing-period';

const moduleLogger = rootLogger.child({ module: 'AuthRoute' });

/**
 * Where to send the user once they are signed in. Set by `_authenticated`'s
 * route guard when it ejects an unauthenticated visitor, so `/app/login`
 * can hand them back to the page they were reaching for.
 *
 * Total by construction — see `optionalCatching`. A junk value drops to
 * absent and the redirect falls through to `/app/home`, which is the same
 * behaviour the previous `z.string().optional().catch(undefined)` had.
 */
const AuthSearchParams = Schema.Struct({
  redirectTo: optionalCatching(Schema.String),
  billingPeriod: optionalCatching(BillingPeriod),
});

type AuthSearchParams = typeof AuthSearchParams.Type;

const authSearchParamsSchema = Schema.toStandardSchemaV1(AuthSearchParams);

export const authRouteOptions = () =>
  ({
    validateSearch: authSearchParamsSchema,
    beforeLoad: async ({
      search,
      preload,
    }: {
      search: AuthSearchParams;
      preload: boolean;
    }) => {
      if (!preload && search.billingPeriod)
        rememberBillingPeriod(search.billingPeriod);
      const check = await getSession();
      // Only a positively verified session skips the login page; on
      // `Unavailable` the page renders and the sign-in attempt itself
      // settles the question.
      if (Predicate.isTagged(check, 'Authenticated')) {
        moduleLogger.info('User already logged in, redirecting');
        throw redirect({
          to: search.redirectTo ?? '/app/home',
        });
      }
    },
  }) satisfies Parameters<ReturnType<typeof createFileRoute>>[0];
